import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Input, OnInit } from '@angular/core'
import { Router } from '@angular/router'
import { catchError, filter, switchMap, takeUntil } from 'rxjs/operators'
import { cloneDeep, differenceBy } from 'lodash-es'
import { Observable, of } from 'rxjs'

import {
  Chat,
  ChatListFilterEnum,
  ChatModel,
  ChatSocketEventEnum,
  ChatSocketResponse,
  ChatUserModel,
  FilterMenuItem,
} from '@shared/models/messenger/messenger.model'
import { MessengerService } from '@app/modules/client/messenger/services/messenger.service'
import { MessengerApiService } from '@app/modules/client/messenger/services/messenger-api.service'
import { DestroyService } from '@core/services/destroy.service'
import { PaginationData } from '@shared/models/pages-data.model'
import { ChatSocketService } from '../../services/chat-socket.service'
import { User } from '@shared/models/user.model'
import { AuthService } from '@app/modules/auth/auth.service'
import { PermissionsService } from '@app/shared/services/permissions.service'
import { PermissionsSource } from '@core/types/permissions.source'
import { sameId } from '@core/helpers/same-id'

@Component({
  selector: 'lin-chat-list',
  templateUrl: './chat-list.component.html',
  styleUrls: ['./chat-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DestroyService],
  standalone: false,
})
export class ChatListComponent implements OnInit {
  selectedChatId: number | null = null
  chatList: ChatModel[] = []
  chatList$: Observable<ChatModel[]>
  filterItems: FilterMenuItem[] = [
    {
      value: null,
      title: 'CLIENT.MESSENGER.CHAT_LIST.ALL_INBOX',
    },
    {
      value: ChatListFilterEnum.INDIVIDUAL,
      title: 'CLIENT.MESSENGER.CHAT_LIST.INDIVIDUAL',
    },
    {
      value: ChatListFilterEnum.GROUP,
      title: 'CLIENT.MESSENGER.CHAT_LIST.GROUP',
    },
  ]
  filterValue: ChatListFilterEnum = null
  isLoading$: Observable<boolean>
  currentUser: User
  isAllForAdmin = 0

  get isAdmin(): boolean {
    return this.currentUser.role.name === 'admin'
  }

  get isShowAll(): boolean {
    return this.permissionsService.hasPermissionSync(PermissionsSource['MESSENGER.SHOW_ALL'])
  }

  @HostBinding('class.full-width') @Input() isFullWidth: boolean

  private page = 1
  private limit = 20
  private total: number
  private searchValue: string

  constructor(
    public readonly messengerService: MessengerService,
    private readonly router: Router,
    private readonly messengerApiService: MessengerApiService,
    private readonly authService: AuthService,
    private readonly destroyService: DestroyService,
    private readonly chatSocketService: ChatSocketService,
    private readonly cdr: ChangeDetectorRef,
    private readonly permissionsService: PermissionsService,
  ) {}

  ngOnInit(): void {
    this.chatList$ = this.messengerService.chatList$
    this.isLoading$ = this.messengerService.isLoading$
    const chatListNext = (chatList: ChatModel[]): ChatModel[] => (this.chatList = chatList)
    this.chatList$.subscribe(chatListNext)

    this.initCurrentUser()
    this.subscribeSelectedChatId()
    this.getChatListData()
    this.subscribePrivateChannel()
    this.subscribeTeamChannel()
  }

  trackById(_, { id }: ChatModel): number {
    return id
  }

  scrollHandler(): void {
    if (this.page * this.limit >= this.total) return

    this.page = this.page + 1
    this.getChatListData()
  }

  handleFilter(filterValue: ChatListFilterEnum, force?: boolean): void {
    if (filterValue === this.filterValue && !force) return

    this.filterValue = filterValue
    this.page = 1
    this.cdr.detectChanges()

    const filterValueSubscribe = ({ data, total }: PaginationData<ChatModel[]>): void => {
      this.messengerService.setChatList(data)
      this.total = total

      this.messengerService.setLoading(false)
    }

    this.getChatList().subscribe(filterValueSubscribe)
  }

  handleLoadAllChats(): void {
    this.isAllForAdmin = this.isAllForAdmin ? 0 : 1
    this.handleFilter(this.filterValue, true)
  }

  handleSearch(searchValue: string): void {
    this.searchValue = searchValue
    this.getChatListData()
  }

  private initCurrentUser(): void {
    this.currentUser = this.authService.getCurrentUser()
  }

  private subscribeSelectedChatId(): void {
    const selectedChatIdSubscribe = (id: number): number => {
      this.selectedChatId = id

      return this.selectedChatId
    }

    this.messengerService.selectedChatId$.pipe(filter(Boolean), takeUntil(this.destroyService)).subscribe(selectedChatIdSubscribe)
  }

  private getChatListData(): void {
    const chatListDataSubscribe = ({ data, total }: PaginationData<ChatModel[]>): void => {
      const diffData: ChatModel[] = differenceBy(data, this.chatList, 'id')
      const chatListUniq = !this.searchValue ? [...this.chatList, ...diffData] : data

      this.messengerService.setChatList(chatListUniq)
      this.total = total

      this.messengerService.setLoading(false)
    }

    this.getChatList().subscribe(chatListDataSubscribe)
  }

  private getChatList = (): Observable<PaginationData<ChatModel[]>> => {
    this.messengerService.setLoading(true)

    return this.messengerApiService
      .getChatList({
        page: this.page,
        limit: this.limit,
        ...(this.filterValue && { scope: this.filterValue }),
        ...(this.searchValue && { name: this.searchValue }),
        ...(this.isAllForAdmin && { isAllForAdmin: this.isAllForAdmin }),
      })
      .pipe(
        // A failed list fetch must still resolve the loader and show the empty state
        catchError(() => of({ data: [], total: 0 } as PaginationData<ChatModel[]>)),
        takeUntil(this.destroyService),
      )
  }

  private subscribePrivateChannel(): void {
    this.chatSocketService.stateCentrifuge$
      .pipe(switchMap(this.chatSocketService.connectPrivateChannel), takeUntil(this.destroyService))
      .subscribe(this.connectPrivateChannelSubscribe)
  }

  private connectPrivateChannelSubscribe = (response: ChatSocketResponse): void => {
    const {
      data: {
        event,
        data: { chat, message },
      },
    } = response

    if (!chat) return

    const findUpdateIndex = (chatModel: ChatModel): boolean => sameId(chatModel.id, chat.id)
    const updateIndex = this.chatList.findIndex(findUpdateIndex)
    const updateChatList = this.chatList.slice()
    const updateChat = updateChatList[updateIndex]

    switch (event) {
      case ChatSocketEventEnum.EVENT_CHAT_CREATED: {
        this.messengerService.setChatList([chat, ...updateChatList])

        break
      }

      case ChatSocketEventEnum.EVENT_MESSAGE_SENT: {
        if (!sameId(this.selectedChatId, chat.id)) {
          chat.unreadCount = (updateChat?.unreadCount || 0) + 1
        }

        if (updateIndex > -1) {
          updateChatList.splice(updateIndex, 1)
        }

        updateChatList.unshift(chat)
        this.messengerService.setChatList(updateChatList)

        break
      }

      case ChatSocketEventEnum.EVENT_MESSAGE_READ: {
        let unreadCounter: number

        if (sameId(this.selectedChatId, chat.id) && updateChat) {
          if (sameId(message.id, updateChat.lastChatHistory.id)) {
            unreadCounter = 0
          } else {
            unreadCounter = updateChat?.unreadCount > 0 ? updateChat?.unreadCount - 1 : 0
          }

          updateChatList[updateIndex].unreadCount = unreadCounter
          updateChatList[updateIndex] = cloneDeep(updateChatList[updateIndex])
          this.messengerService.setChatList(updateChatList)
        }

        break
      }

      case ChatSocketEventEnum.EVENT_MESSAGE_EDITED:
      case ChatSocketEventEnum.EVENT_CHAT_EDITED: {
        updateChatList[updateIndex] = chat
        this.messengerService.setChatList(updateChatList)
        break
      }

      case ChatSocketEventEnum.EVENT_CHAT_DELETED: {
        if (updateIndex < 0) break

        this.privateChannelUpdate(updateChatList, updateIndex)

        break
      }

      case ChatSocketEventEnum.EVENT_USERS_DELETED_FROM_CHAT: {
        const isCurrentChat = sameId(chat?.id, updateChat.id)
        const findMe = (user: ChatUserModel): boolean => sameId(user.id, this.currentUser.id)
        const isInUsers = chat.users.find(findMe)

        if (isCurrentChat && sameId(this.selectedChatId, chat.id) && !isInUsers) {
          this.privateChannelUpdate(updateChatList, updateIndex)
        }

        break
      }
    }

    this.messengerService.setUnreadCount(this.calculateUnreadMessage())
  }

  private subscribeTeamChannel(): void {
    this.chatSocketService.stateCentrifuge$
      .pipe(switchMap(this.chatSocketService.connectTeamChannel), takeUntil(this.destroyService))
      .subscribe(this.teamChannelSubscribe)
  }

  private teamChannelSubscribe = (response: ChatSocketResponse) => {
    const {
      data: { event, user },
    } = response

    if (!this.currentUser || sameId(user.id, this.currentUser.id)) {
      return
    }

    switch (event) {
      case ChatSocketEventEnum.EVENT_USER_STATUS_UPDATED: {
        const updateChatList = this.chatList.slice()
        const findUpdatedUser = (userChat: ChatUserModel): boolean => sameId(userChat.id, user.id)
        const updateUsersInChatList = (chat: ChatModel): ChatModel => {
          const userUpdatedIndex = chat.users.findIndex(findUpdatedUser)

          if (userUpdatedIndex > -1) {
            chat.users[userUpdatedIndex].networkStatus = user.networkStatus
            return new Chat(chat)
          }

          return chat
        }

        this.messengerService.setChatList(updateChatList.map(updateUsersInChatList))
        break
      }
    }
  }

  private calculateUnreadMessage = (): number => {
    const calculateCount = (previousValue: number, currentValue: number): number => previousValue + currentValue
    const toUnreadCount = (chat: ChatModel): number => chat.unreadCount || 0

    return this.chatList.map(toUnreadCount).reduce(calculateCount, 0)
  }

  private privateChannelUpdate(updateChatList: ChatModel[], updateIndex: number): void {
    const prevId = updateChatList[updateIndex - 1]?.id
    const nextId = updateChatList[updateIndex + 1]?.id

    updateChatList.splice(updateIndex, 1)
    this.messengerService.setChatList(updateChatList)

    const routeId = prevId || nextId || updateChatList[updateIndex]?.id

    routeId && void this.router.navigate(['client', 'messenger', routeId])
  }
}
